/**
 * Supplier Management Page
 * 
 * CRUD operations for supplier management with search, filtering, and bulk operations
 */

import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import {
  Box,
  Card,
  CardContent,
  Typography,
  Button,
  TextField,
  IconButton,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  TablePagination,
  Chip,
  Avatar,
  Menu,
  MenuItem,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  FormControl,
  InputLabel,
  Select,
  Checkbox,
  ListItemText,
  Tooltip,
  Alert,
  CircularProgress,
  Grid,
  Paper,
  Snackbar,
  ToggleButton,
  ToggleButtonGroup,
} from '@mui/material';
import {
  Add as AddIcon,
  Search as SearchIcon,
  FilterList as FilterIcon,
  MoreVert as MoreVertIcon,
  Edit as EditIcon,
  Delete as DeleteIcon,
  Visibility as VisibilityIcon,
  Business as BusinessIcon,
  Phone as PhoneIcon,
  Email as EmailIcon,
  LocationOn as LocationIcon,
  Star as StarIcon,
  GetApp as GetAppIcon,
  Upload as UploadIcon,
  CheckCircle as ActivateIcon,
  Cancel as DeactivateIcon,
  AccountBalance as LedgerIcon,
  ViewModule,
  ViewList,
} from '@mui/icons-material';
import { PurchaseService } from '../services/purchaseService';
import {
  Supplier,
  SupplierListParams,
  SupplierListResponse,
} from '../types/purchase';
import { formatCurrency, formatDate } from '../utils/formatters';
import BulkActionBar from '../components/ui/BulkActionBar';
import ProgressManager from '../components/ui/ProgressManager';
import useBulkOperations from '../hooks/useBulkOperations';
import PartyCard from '../components/party/PartyCard';
import AddressEditModal from '../components/party/AddressEditModal';
import NotificationEditModal from '../components/party/NotificationEditModal';
import { PartyAddressService } from '../services/partyAddressService';
import * as notificationService from '../services/partyNotificationService';

const SupplierManagementMUI: React.FC = () => {
  const navigate = useNavigate();
  const [suppliers, setSuppliers] = useState<Supplier[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [totalCount, setTotalCount] = useState(0);
  const [successMessage, setSuccessMessage] = useState<string | null>(null);
  
  // Bulk operations
  const { 
    operations, 
    executeBulkOperation, 
    clearCompletedOperations, 
    clearAllOperations 
  } = useBulkOperations()
  
  // Pagination and filtering
  const [page, setPage] = useState(0);
  const [rowsPerPage, setRowsPerPage] = useState(25);
  const [searchQuery, setSearchQuery] = useState('');
  const [filters, setFilters] = useState<SupplierListParams>({});
  
  // View mode state
  const [viewMode, setViewMode] = useState<'card' | 'table'>('table');
  
  // Modal states
  const [addressModalOpen, setAddressModalOpen] = useState(false);
  const [notificationModalOpen, setNotificationModalOpen] = useState(false);
  const [selectedParty, setSelectedParty] = useState<any>(null);
  const [editingAddress, setEditingAddress] = useState<any>(null);
  
  // UI state
  const [selectedSuppliers, setSelectedSuppliers] = useState<string[]>([]);
  const [anchorEl, setAnchorEl] = useState<null | HTMLElement>(null);
  const [selectedSupplierId, setSelectedSupplierId] = useState<string | null>(null);
  const [filterDialogOpen, setFilterDialogOpen] = useState(false);
  const [deleteDialogOpen, setDeleteDialogOpen] = useState(false);
  const [supplierToDelete, setSupplierToDelete] = useState<Supplier | null>(null);
  const [supplierBalance, setSupplierBalance] = useState<number>(0);

  useEffect(() => {
    loadSuppliers();
  }, [page, rowsPerPage, searchQuery, filters]);

  const loadSuppliers = async () => {
    try {
      setLoading(true);
      setError(null);
      
      const params: SupplierListParams = {
        page: page + 1,
        limit: rowsPerPage,
        search: searchQuery || undefined,
        ...filters,
      };
      
      const response: SupplierListResponse = await PurchaseService.getSuppliers(params);
      setSuppliers(response.items);
      setTotalCount(response.total);
    } catch (err: any) {
      console.error('Failed to load suppliers:', err);
      setError(err.message || 'Failed to load suppliers');
    } finally {
      setLoading(false);
    }
  };

  const handleSearch = (event: React.ChangeEvent<HTMLInputElement>) => {
    setSearchQuery(event.target.value);
    setPage(0);
  };

  const handlePageChange = (event: unknown, newPage: number) => {
    setPage(newPage);
  };

  const handleRowsPerPageChange = (event: React.ChangeEvent<HTMLInputElement>) => {
    setRowsPerPage(parseInt(event.target.value, 10));
    setPage(0);
  };

  const handleSelectSupplier = (supplierId: string) => {
    setSelectedSuppliers(prev => 
      prev.includes(supplierId)
        ? prev.filter(id => id !== supplierId)
        : [...prev, supplierId]
    );
  };

  const handleSelectAllSuppliers = (event: React.ChangeEvent<HTMLInputElement>) => {
    if (event.target.checked) {
      setSelectedSuppliers(suppliers.map(supplier => supplier.id));
    } else {
      setSelectedSuppliers([]);
    }
  };

  const handleMenuOpen = (event: React.MouseEvent<HTMLElement>, supplierId: string) => {
    setAnchorEl(event.currentTarget);
    setSelectedSupplierId(supplierId);
  };

  const handleMenuClose = () => {
    setAnchorEl(null);
    setSelectedSupplierId(null);
  };

  const handleViewSupplier = (supplierId: string) => {
    navigate(`/admin/parties/suppliers/${supplierId}`);
    handleMenuClose();
  };

  const handleEditSupplier = (supplierId: string) => {
    navigate(`/admin/parties/suppliers/${supplierId}/edit`);
    handleMenuClose();
  };

  const handleViewLedger = (supplierId: string) => {
    navigate(`/admin/parties/suppliers/${supplierId}/ledger`);
    handleMenuClose();
  };

  const handleDeleteSupplier = (supplier: Supplier) => {
    // Fetch supplier balance (you may need to add an API endpoint for this)
    // For now, we'll use 0 as placeholder - in production, fetch from ledger
    setSupplierBalance(0);
    setSupplierToDelete(supplier);
    setDeleteDialogOpen(true);
    handleMenuClose();
  };

  const confirmDeleteSupplier = async () => {
    if (!supplierToDelete) return;
    
    try {
      await PurchaseService.deleteSupplier(supplierToDelete.id);
      setDeleteDialogOpen(false);
      setSupplierToDelete(null);
      loadSuppliers();
    } catch (err: any) {
      console.error('Failed to delete supplier:', err);
      setError(err.message || 'Failed to delete supplier');
    }
  };

  const handleApplyFilters = (newFilters: SupplierListParams) => {
    setFilters(newFilters);
    setPage(0);
    setFilterDialogOpen(false);
  };

  const handleExportSuppliers = async () => {
    try {
      // Implementation would depend on backend API
      console.log('Exporting suppliers...');
    } catch (err: any) {
      console.error('Failed to export suppliers:', err);
      setError(err.message || 'Failed to export suppliers');
    }
  };

  const handleImportSuppliers = async (file: File) => {
    try {
      const result = await PurchaseService.importSuppliers(file);
      console.log('Import result:', result);
      loadSuppliers();
    } catch (err: any) {
      console.error('Failed to import suppliers:', err);
      setError(err.message || 'Failed to import suppliers');
    }
  };

  // Party Management Handlers
  const handleEditAddress = (party: any, type: 'billing' | 'shipping') => {
    setSelectedParty(party);
    setEditingAddress(party.address);
    setAddressModalOpen(true);
  };

  const handleEditNotifications = (party: any) => {
    setSelectedParty(party);
    setNotificationModalOpen(true);
  };

  const handleSaveAddress = async (address: any) => {
    if (!selectedParty) return;
    
    try {
      await PartyAddressService.updateSupplierAddress(selectedParty.id, address);
      setSuccessMessage('Address updated successfully');
      loadSuppliers();
      setAddressModalOpen(false);
    } catch (error) {
      console.error('Error saving address:', error);
      setError('Failed to save address');
      throw error;
    }
  };

  const handleSaveNotifications = async (preferences: any) => {
    if (!selectedParty) return;
    
    try {
      await notificationService.updateSupplierNotifications(selectedParty.id, preferences);
      setSuccessMessage('Notification preferences updated successfully');
      loadSuppliers();
    } catch (error) {
      console.error('Error saving notifications:', error);
      setError('Failed to update notification preferences');
      throw error;
    }
  };

  const handleSendTest = async (type: 'email' | 'sms' | 'whatsapp') => {
    if (!selectedParty) return;
    
    try {
      await notificationService.sendSupplierTestNotification(selectedParty.id, type);
      setSuccessMessage(`Test ${type} sent successfully`);
    } catch (error) {
      console.error('Error sending test notification:', error);
      setError(`Failed to send test ${type}`);
    }
  };

  const getPerformanceColor = (rating: number) => {
    if (rating >= 4.5) return 'success';
    if (rating >= 3.5) return 'warning';
    return 'error';
  };

  // Bulk operation handlers
  const handleSupplierActivate = async (selectedIds: string[]) => {
    await executeBulkOperation(
      selectedIds,
      {
        title: 'Activate Suppliers',
        description: `Activating ${selectedIds.length} suppliers`,
        totalItems: selectedIds.length,
      },
      async (items, onProgress) => {
        try {
          onProgress(0, 'Starting bulk activation...')
          
          const result = await PurchaseService.bulkUpdateSupplierStatus(items, true)
          
          onProgress(items.length, 'Bulk activation completed')
          
          // Refetch suppliers to show updated data
          loadSuppliers()
          
          setSuccessMessage(`Successfully activated ${result.successful.length} suppliers`)
          if (result.failed.length > 0) {
            setError(`Failed to activate ${result.failed.length} suppliers`)
          }
          
          return {
            successful: result.successful.length,
            failed: result.failed.length,
            warnings: 0,
            errors: result.failed.map(f => f.error || 'Unknown error'),
          }
        } catch (error: any) {
          setError(error?.response?.data?.detail || 'Failed to activate suppliers')
          return {
            successful: 0,
            failed: items.length,
            warnings: 0,
            errors: [error?.response?.data?.detail || 'Failed to activate suppliers'],
          }
        }
      }
    )
    setSelectedSuppliers([])
  }

  const handleSupplierDeactivate = async (selectedIds: string[]) => {
    // Confirm deactivation
    if (!window.confirm(`Are you sure you want to deactivate ${selectedIds.length} suppliers?`)) {
      return
    }

    await executeBulkOperation(
      selectedIds,
      {
        title: 'Deactivate Suppliers',
        description: `Deactivating ${selectedIds.length} suppliers`,
        totalItems: selectedIds.length,
      },
      async (items, onProgress) => {
        try {
          onProgress(0, 'Starting bulk deactivation...')
          
          const result = await PurchaseService.bulkUpdateSupplierStatus(items, false)
          
          onProgress(items.length, 'Bulk deactivation completed')
          
          // Refetch suppliers to show updated data
          loadSuppliers()
          
          setSuccessMessage(`Successfully deactivated ${result.successful.length} suppliers`)
          if (result.failed.length > 0) {
            setError(`Failed to deactivate ${result.failed.length} suppliers`)
          }
          
          return {
            successful: result.successful.length,
            failed: result.failed.length,
            warnings: 0,
            errors: result.failed.map(f => f.error || 'Unknown error'),
          }
        } catch (error: any) {
          setError(error?.response?.data?.detail || 'Failed to deactivate suppliers')
          return {
            successful: 0,
            failed: items.length,
            warnings: 0,
            errors: [error?.response?.data?.detail || 'Failed to deactivate suppliers'],
          }
        }
      }
    )
    setSelectedSuppliers([])
  }

  const handleSupplierExport = async (selectedIds: string[]) => {
    await executeBulkOperation(
      selectedIds,
      {
        title: 'Export Suppliers',
        description: `Exporting ${selectedIds.length} suppliers to CSV`,
        totalItems: selectedIds.length,
      },
      async (items, onProgress) => {
        try {
          onProgress(0, 'Preparing export...')
          
          const blob = await PurchaseService.bulkExportSuppliers(items, 'csv')
          
          onProgress(items.length, 'Export completed, downloading file...')
          
          // Create download link
          const url = URL.createObjectURL(blob)
          const a = document.createElement('a')
          a.href = url
          const timestamp = new Date().toISOString().split('T')[0]
          a.download = `suppliers_export_${timestamp}.csv`
          document.body.appendChild(a)
          a.click()
          document.body.removeChild(a)
          URL.revokeObjectURL(url)
          
          setSuccessMessage(`Successfully exported ${items.length} suppliers`)
          
          return {
            successful: items.length,
            failed: 0,
            warnings: 0,
            errors: [],
          }
        } catch (error: any) {
          setError(error?.response?.data?.detail || 'Failed to export suppliers')
          return {
            successful: 0,
            failed: items.length,
            warnings: 0,
            errors: [error?.response?.data?.detail || 'Failed to export suppliers'],
          }
        }
      }
    )
    setSelectedSuppliers([])
  }

  const handleSupplierDelete = async (selectedIds: string[]) => {
    // Confirm deletion
    if (!window.confirm(`Are you sure you want to delete ${selectedIds.length} suppliers? This action cannot be undone.`)) {
      return
    }

    await executeBulkOperation(
      selectedIds,
      {
        title: 'Delete Suppliers',
        description: `Deleting ${selectedIds.length} suppliers`,
        totalItems: selectedIds.length,
      },
      async (items, onProgress) => {
        let successful = 0
        let failed = 0
        const errors: string[] = []
        
        for (let i = 0; i < items.length; i++) {
          try {
            await PurchaseService.deleteSupplier(items[i])
            successful++
            onProgress(i + 1, `Deleted supplier ${i + 1}`)
          } catch (error: any) {
            failed++
            errors.push(error?.response?.data?.detail || 'Unknown error')
          }
        }
        
        // Refetch suppliers to show updated data
        loadSuppliers()
        
        if (successful > 0) {
          setSuccessMessage(`Successfully deleted ${successful} suppliers`)
        }
        if (failed > 0) {
          setError(`Failed to delete ${failed} suppliers`)
        }
        
        return { successful, failed, warnings: 0, errors }
      }
    )
    setSelectedSuppliers([])
  }

  const supplierBulkActions = [
    {
      id: 'activate',
      label: 'Activate',
      icon: <ActivateIcon />,
      onClick: handleSupplierActivate,
      color: 'success' as const,
    },
    {
      id: 'deactivate',
      label: 'Deactivate',
      icon: <DeactivateIcon />,
      onClick: handleSupplierDeactivate,
      color: 'warning' as const,
    },
    {
      id: 'export',
      label: 'Export',
      icon: <GetAppIcon />,
      onClick: handleSupplierExport,
      color: 'primary' as const,
    },
    {
      id: 'delete',
      label: 'Delete',
      icon: <DeleteIcon />,
      onClick: handleSupplierDelete,
      color: 'error' as const,
    },
  ]

  if (error) {
    return (
      <Box p={3}>
        <Alert severity="error" action={
          <Button color="inherit" size="small" onClick={loadSuppliers}>
            Retry
          </Button>
        }>
          {error}
        </Alert>
      </Box>
    );
  }

  return (
    <Box p={3}>
      {/* Progress Manager for bulk operations */}
      <ProgressManager
        operations={operations}
        onClearCompleted={clearCompletedOperations}
        onClearAll={clearAllOperations}
      />

      {/* Success Message Snackbar */}
      <Snackbar
        open={!!successMessage}
        autoHideDuration={6000}
        onClose={() => setSuccessMessage(null)}
        message={successMessage}
      />

      {/* Header */}
      <Box display="flex" justifyContent="between" alignItems="center" mb={3}>
        <Box>
          <Typography variant="h4" component="h1" gutterBottom sx={{ fontWeight: 600, color: '#0d47a1' }}>
            Supplier Management
          </Typography>
          <Typography variant="body1" color="text.secondary">
            Manage your suppliers and vendor relationships
          </Typography>
        </Box>
        <Box display="flex" gap={2}>
          <Button
            variant="outlined"
            color="primary"
            startIcon={<GetAppIcon />}
            onClick={handleExportSuppliers}
            aria-label="Export suppliers"
            sx={{ 
              textTransform: 'none', 
              fontWeight: 500, 
              borderRadius: 2
            }}
          >
            Export
          </Button>
          <Button
            variant="outlined"
            color="primary"
            startIcon={<UploadIcon />}
            component="label"
            aria-label="Import suppliers"
            sx={{ 
              textTransform: 'none', 
              fontWeight: 500, 
              borderRadius: 2
            }}
          >
            Import
            <input
              type="file"
              hidden
              accept=".csv,.xlsx"
              onChange={(e) => {
                const file = e.target.files?.[0];
                if (file) handleImportSuppliers(file);
              }}
              aria-label="Select file to import"
            />
          </Button>
          <Button
            variant="contained"
            color="secondary"
            startIcon={<AddIcon />}
            onClick={() => navigate('/admin/parties/suppliers/create')}
            aria-label="Add new supplier"
            sx={{ 
              textTransform: 'none', 
              fontWeight: 500, 
              borderRadius: 2,
              boxShadow: '0 2px 4px rgba(0,0,0,0.1)',
              '&:hover': {
                boxShadow: '0 4px 8px rgba(0,0,0,0.15)',
              }
            }}
          >
            Add Supplier
          </Button>
        </Box>
      </Box>

      {/* Bulk Action Bar */}
      <BulkActionBar
        selectedItems={selectedSuppliers}
        totalItems={suppliers.length}
        onSelectAll={(selected) => {
          if (selected) {
            setSelectedSuppliers(suppliers.map(s => s.id))
          } else {
            setSelectedSuppliers([])
          }
        }}
        onClearSelection={() => setSelectedSuppliers([])}
        actions={supplierBulkActions}
        itemName="suppliers"
      />

      {/* Search and Filters */}
      <Card sx={{ 
        mb: 3, 
        borderRadius: 3,
        boxShadow: '0 2px 8px rgba(0,0,0,0.1)',
      }}>
        <CardContent>
          <Grid container spacing={2} alignItems="center">
            <Grid item xs={12} md={6}>
              <TextField
                fullWidth
                placeholder="Search suppliers by name, contact person, or phone..."
                value={searchQuery}
                onChange={handleSearch}
                InputProps={{
                  startAdornment: <SearchIcon sx={{ mr: 1, color: 'text.secondary' }} />,
                }}
                inputProps={{
                  'aria-label': 'Search suppliers by name, contact person, phone, email, or GSTIN',
                }}
                sx={{
                  '& .MuiOutlinedInput-root': {
                    borderRadius: 2,
                  }
                }}
              />
            </Grid>
            <Grid item xs={12} md={6}>
              <Box display="flex" gap={2} justifyContent="flex-end">
                <ToggleButtonGroup
                  value={viewMode}
                  exclusive
                  onChange={(e, newMode) => newMode && setViewMode(newMode)}
                  size="small"
                  sx={{ mr: 1 }}
                  aria-label="View mode"
                >
                  <ToggleButton value="table" aria-label="table view">
                    <Tooltip title="Table View">
                      <ViewList />
                    </Tooltip>
                  </ToggleButton>
                  <ToggleButton value="card" aria-label="card view">
                    <Tooltip title="Card View">
                      <ViewModule />
                    </Tooltip>
                  </ToggleButton>
                </ToggleButtonGroup>
                <Button
                  variant="outlined"
                  color="primary"
                  startIcon={<FilterIcon />}
                  onClick={() => setFilterDialogOpen(true)}
                  aria-label="Open filter options"
                  sx={{ 
                    textTransform: 'none', 
                    fontWeight: 500, 
                    borderRadius: 2
                  }}
                >
                  Filters
                </Button>
              </Box>
            </Grid>
          </Grid>
        </CardContent>
      </Card>

      {/* Suppliers Table/Card View */}
      {viewMode === 'table' ? (
        <Card sx={{ 
          borderRadius: 3,
          boxShadow: '0 2px 8px rgba(0,0,0,0.1)',
        }}>
          <TableContainer>
            <Table aria-label="Suppliers table">
              <TableHead>
                <TableRow sx={{ 
                  backgroundColor: '#0d47a1',
                }}>
                  <TableCell padding="checkbox" sx={{ color: '#ffffff' }}>
                    <Checkbox
                      indeterminate={selectedSuppliers.length > 0 && selectedSuppliers.length < suppliers.length}
                      checked={suppliers.length > 0 && selectedSuppliers.length === suppliers.length}
                      onChange={handleSelectAllSuppliers}
                      inputProps={{
                        'aria-label': 'Select all suppliers',
                      }}
                      sx={{
                        color: 'rgba(255, 255, 255, 0.7)',
                        '&.Mui-checked': {
                          color: '#ffffff',
                        },
                        '&.MuiCheckbox-indeterminate': {
                          color: '#ffffff',
                        }
                      }}
                    />
                  </TableCell>
                  <TableCell sx={{ color: '#ffffff', fontWeight: 600 }}>Supplier</TableCell>
                  <TableCell sx={{ color: '#ffffff', fontWeight: 600 }}>Contact</TableCell>
                  <TableCell sx={{ color: '#ffffff', fontWeight: 600 }}>Location</TableCell>
                  <TableCell align="center" sx={{ color: '#ffffff', fontWeight: 600 }}>Performance</TableCell>
                  <TableCell align="right" sx={{ color: '#ffffff', fontWeight: 600 }}>Total Purchases</TableCell>
                  <TableCell align="center" sx={{ color: '#ffffff', fontWeight: 600 }}>Status</TableCell>
                  <TableCell align="center" sx={{ color: '#ffffff', fontWeight: 600 }}>Actions</TableCell>
                </TableRow>
              </TableHead>
              <TableBody>
                {loading ? (
                  <TableRow>
                    <TableCell colSpan={8} align="center" sx={{ py: 4 }}>
                      <CircularProgress />
                    </TableCell>
                  </TableRow>
                ) : suppliers.length === 0 ? (
                  <TableRow>
                    <TableCell colSpan={8} align="center" sx={{ py: 4 }}>
                      <Typography variant="body1" color="text.secondary">
                        No suppliers found
                      </Typography>
                    </TableCell>
                  </TableRow>
                ) : (
                  suppliers.map((supplier) => (
                  <TableRow 
                    key={supplier.id} 
                    hover
                    sx={{
                      '&:hover': {
                        backgroundColor: 'rgba(33, 150, 243, 0.04)',
                      }
                    }}
                  >
                    <TableCell padding="checkbox">
                      <Checkbox
                        checked={selectedSuppliers.includes(supplier.id)}
                        onChange={() => handleSelectSupplier(supplier.id)}
                        inputProps={{
                          'aria-label': `Select ${supplier.businessName}`,
                        }}
                      />
                    </TableCell>
                    <TableCell>
                      <Box display="flex" alignItems="center" gap={2}>
                        <Avatar sx={{ bgcolor: 'primary.main' }}>
                          <BusinessIcon />
                        </Avatar>
                        <Box>
                          <Typography variant="body2" fontWeight="medium">
                            {supplier.businessName}
                          </Typography>
                          <Typography variant="caption" color="text.secondary">
                            {supplier.contactPerson}
                          </Typography>
                          {supplier.gstin && (
                            <Typography variant="caption" display="block" color="text.secondary">
                              GSTIN: {supplier.gstin}
                            </Typography>
                          )}
                        </Box>
                      </Box>
                    </TableCell>
                    <TableCell>
                      <Box>
                        <Box display="flex" alignItems="center" gap={1} mb={0.5}>
                          <PhoneIcon fontSize="small" color="action" />
                          <Typography variant="body2">{supplier.phone}</Typography>
                        </Box>
                        {supplier.email && (
                          <Box display="flex" alignItems="center" gap={1}>
                            <EmailIcon fontSize="small" color="action" />
                            <Typography variant="body2">{supplier.email}</Typography>
                          </Box>
                        )}
                      </Box>
                    </TableCell>
                    <TableCell>
                      <Box display="flex" alignItems="center" gap={1}>
                        <LocationIcon fontSize="small" color="action" />
                        <Typography variant="body2">
                          {supplier.address.city}, {supplier.address.state}
                        </Typography>
                      </Box>
                    </TableCell>
                    <TableCell align="center">
                      <Box display="flex" alignItems="center" justifyContent="center" gap={1}>
                        <StarIcon fontSize="small" color={getPerformanceColor(Number(supplier.performanceRating) || 0)} />
                        <Typography variant="body2" color={`${getPerformanceColor(Number(supplier.performanceRating) || 0)}.main`}>
                          {(Number(supplier.performanceRating) || 0).toFixed(1)}
                        </Typography>
                      </Box>
                    </TableCell>
                    <TableCell align="right">
                      <Typography variant="body2" fontWeight="medium">
                        {supplier.totalPurchases ? formatCurrency(supplier.totalPurchases) : '-'}
                      </Typography>
                      {supplier.lastPurchaseDate && (
                        <Typography variant="caption" color="text.secondary" display="block">
                          Last: {formatDate(supplier.lastPurchaseDate)}
                        </Typography>
                      )}
                    </TableCell>
                    <TableCell align="center">
                      <Chip
                        label={supplier.isActive ? 'Active' : 'Inactive'}
                        color={supplier.isActive ? 'success' : 'default'}
                        size="small"
                        sx={{ borderRadius: 4, fontWeight: 500 }}
                      />
                    </TableCell>
                    <TableCell align="center">
                      <IconButton
                        size="small"
                        onClick={(e) => handleMenuOpen(e, supplier.id)}
                        aria-label={`Actions for ${supplier.businessName}`}
                        aria-haspopup="true"
                      >
                        <MoreVertIcon />
                      </IconButton>
                    </TableCell>
                  </TableRow>
                ))
              )}
            </TableBody>
          </Table>
        </TableContainer>
        
        <TablePagination
          rowsPerPageOptions={[10, 25, 50, 100]}
          component="div"
          count={totalCount}
          rowsPerPage={rowsPerPage}
          page={page}
          onPageChange={handlePageChange}
          onRowsPerPageChange={handleRowsPerPageChange}
          aria-label="Suppliers table pagination"
        />
      </Card>
      ) : (
        <Grid container spacing={3} sx={{ p: 3 }}>
          {suppliers.map((supplier) => (
            <Grid item xs={12} sm={6} md={4} lg={3} key={supplier.id}>
              <PartyCard
                party={{
                  ...supplier,
                  type: 'supplier' as const,
                  name: supplier.businessName || '',
                  is_active: supplier.isActive ?? true,
                }}
                onEdit={(party) => handleEditSupplier(party.id)}
                onDelete={(id) => {
                  const supplier = suppliers.find(s => s.id === id);
                  if (supplier) handleDeleteSupplier(supplier);
                }}
                onViewLedger={(party) => handleViewLedger(party.id)}
                onEditAddress={handleEditAddress}
                onEditNotifications={handleEditNotifications}
              />
            </Grid>
          ))}
        </Grid>
      )}

      {/* Action Menu */}
      <Menu
        anchorEl={anchorEl}
        open={Boolean(anchorEl)}
        onClose={handleMenuClose}
        aria-label="Supplier actions menu"
      >
        <MenuItem onClick={() => selectedSupplierId && handleViewSupplier(selectedSupplierId)}>
          <VisibilityIcon fontSize="small" sx={{ mr: 1 }} />
          View Details
        </MenuItem>
        <MenuItem onClick={() => selectedSupplierId && handleViewLedger(selectedSupplierId)}>
          <LedgerIcon fontSize="small" sx={{ mr: 1 }} />
          View Ledger
        </MenuItem>
        <MenuItem onClick={() => selectedSupplierId && handleEditSupplier(selectedSupplierId)}>
          <EditIcon fontSize="small" sx={{ mr: 1 }} />
          Edit Supplier
        </MenuItem>
        <MenuItem 
          onClick={() => {
            const supplier = suppliers.find(s => s.id === selectedSupplierId);
            if (supplier) handleDeleteSupplier(supplier);
          }}
          sx={{ color: 'error.main' }}
        >
          <DeleteIcon fontSize="small" sx={{ mr: 1 }} />
          Delete Supplier
        </MenuItem>
      </Menu>

      {/* Delete Confirmation Dialog */}
      <Dialog 
        open={deleteDialogOpen} 
        onClose={() => setDeleteDialogOpen(false)} 
        maxWidth="sm" 
        fullWidth
        aria-labelledby="delete-dialog-title"
        aria-describedby="delete-dialog-description"
      >
        <DialogTitle id="delete-dialog-title">Deactivate Supplier</DialogTitle>
        <DialogContent>
          <Typography id="delete-dialog-description">
            Are you sure you want to deactivate <strong>{supplierToDelete?.businessName}</strong>?
          </Typography>
          {supplierBalance > 0 && (
            <Alert severity="warning" sx={{ mt: 2 }}>
              This supplier has an outstanding payable balance of ₹{supplierBalance.toFixed(2)}. 
              Please ensure all payments are settled before deactivation.
            </Alert>
          )}
          <Typography sx={{ mt: 2 }}>
            Deactivating this supplier will:
          </Typography>
          <Box component="ul" sx={{ mt: 1, pl: 2 }}>
            <li>Prevent new purchase orders from being created</li>
            <li>Preserve all historical data and records</li>
            <li>Allow reactivation at any time</li>
          </Box>
        </DialogContent>
        <DialogActions>
          <Button 
            onClick={() => setDeleteDialogOpen(false)}
            aria-label="Cancel deactivation"
          >
            Cancel
          </Button>
          <Button 
            onClick={confirmDeleteSupplier} 
            color="error" 
            variant="contained"
            aria-label="Confirm deactivation"
          >
            Deactivate
          </Button>
        </DialogActions>
      </Dialog>

      {/* Filter Dialog */}
      <Dialog 
        open={filterDialogOpen} 
        onClose={() => setFilterDialogOpen(false)} 
        maxWidth="sm" 
        fullWidth
        aria-labelledby="filter-dialog-title"
      >
        <DialogTitle id="filter-dialog-title">Filter Suppliers</DialogTitle>
        <DialogContent>
          <Grid container spacing={2} sx={{ mt: 1 }}>
            <Grid item xs={12}>
              <FormControl fullWidth>
                <InputLabel id="filter-status-label">Status</InputLabel>
                <Select
                  labelId="filter-status-label"
                  value={filters.isActive ?? ''}
                  onChange={(e) => setFilters(prev => ({ ...prev, isActive: e.target.value === '' ? undefined : e.target.value === 'true' }))}
                  aria-label="Filter by status"
                >
                  <MenuItem value="">All</MenuItem>
                  <MenuItem value="true">Active</MenuItem>
                  <MenuItem value="false">Inactive</MenuItem>
                </Select>
              </FormControl>
            </Grid>
            <Grid item xs={12}>
              <FormControl fullWidth>
                <InputLabel id="filter-sort-label">Sort By</InputLabel>
                <Select
                  labelId="filter-sort-label"
                  value={filters.sortBy || ''}
                  onChange={(e) => setFilters(prev => ({ ...prev, sortBy: e.target.value }))}
                  aria-label="Sort suppliers by"
                >
                  <MenuItem value="">Default</MenuItem>
                  <MenuItem value="businessName">Business Name</MenuItem>
                  <MenuItem value="performanceRating">Performance Rating</MenuItem>
                  <MenuItem value="createdAt">Date Added</MenuItem>
                </Select>
              </FormControl>
            </Grid>
          </Grid>
        </DialogContent>
        <DialogActions>
          <Button 
            onClick={() => setFilterDialogOpen(false)}
            aria-label="Cancel filters"
          >
            Cancel
          </Button>
          <Button 
            onClick={() => handleApplyFilters(filters)} 
            variant="contained"
            aria-label="Apply filters"
          >
            Apply Filters
          </Button>
        </DialogActions>
      </Dialog>

      {/* Address Edit Modal */}
      <AddressEditModal
        open={addressModalOpen}
        onClose={() => setAddressModalOpen(false)}
        address={editingAddress}
        addressType="billing"
        partyName={selectedParty?.business_name || ''}
        onSave={handleSaveAddress}
      />

      {/* Notification Edit Modal */}
      <NotificationEditModal
        open={notificationModalOpen}
        onClose={() => setNotificationModalOpen(false)}
        partyName={selectedParty?.business_name || ''}
        email={selectedParty?.email}
        mobile={selectedParty?.phone}
        preferences={selectedParty?.supplier_metadata?.communication_preferences}
        onSave={handleSaveNotifications}
        onSendTest={handleSendTest}
      />
    </Box>
  );
};

export default SupplierManagementMUI;